import { buildClusterFaqJsonLdAsync } from "@/lib/clusterFaq";
import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import { ServicePageEnhancer } from "@/components/service/ServicePageEnhancer";
import AnswerBoxTop from "@/components/AnswerBoxTop";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
// /translate — hub listing all 18 language sub-pages
import { createFileRoute, Link } from "@tanstack/react-router";
import { DirectAnswer, KeyFactsTable } from "@/components/seo/Blocks";
import { VoiceSearchFAQ } from "@/components/seo/VoiceSearchFAQ";
import { speakableSchema, serviceWithReviewsSchema, howToSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TRANSLATION_LANGUAGES } from "@/data/translation-languages";
import { consolidateGraph } from "@/lib/schema-graph";
import { Languages, ArrowRight } from "lucide-react";
import { TrustLayer } from "@/components/TrustLayer";
import { TranslateVisaCrossLink } from "@/components/TranslateVisaCrossLink";
import HubCountryLinks from "@/components/HubCountryLinks";
import { RelatedComparisons } from "@/components/RelatedComparisons";
import { AIAnswerBox } from "@/components/AIAnswerBox";
import { claimReviewSchema, softwareApplicationSchema, entitySameAsSchema, COMMON_CLAIM_REBUTTALS_TH } from "@/lib/trust-schema";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/translate/")({
  loader: async () => ({
    clusterFaq: await buildClusterFaqJsonLdAsync("translate", "th", "https://nyclanguageinstitute.com/translate"),
    voiceFaqs: (await import("@/data/voice-faqs")).TRANSLATE_VOICE_FAQS,
  }),
  head: ({ loaderData }: any) => {
    const url = `${SITE}/translate`;
    const title = `แปลเอกสารรายภาษา ${TRANSLATION_LANGUAGES.length}+ ภาษา — NYC Certified Translation`;
    const desc = `บริการแปลเอกสารรับรองครบ ${TRANSLATION_LANGUAGES.length} ภาษา: อังกฤษ ญี่ปุ่น จีน เกาหลี เยอรมัน ฝรั่งเศส สเปน อาหรับ และอีกมากมาย ใช้ยื่นราชการ-สถานทูต-กงสุล ได้ทันที — คุยกับผู้เชี่ยวชาญตัวจริงได้เลยวันนี้`;
    return {
      meta: [
        { title: ctaTitle(title, "/translate", "th") },
        { name: "description", content: ctaDesc(desc, "/translate", "th")},
        { property: "og:title", content: ctaTitle(title, "/translate", "th")},
        { property: "og:description", content: ctaDesc(desc, "/translate", "th")},
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks("/translate")],
      scripts: [speakableSchema(),
        howToSchema({
          name: "วิธีสั่งแปลเอกสารรับรองกับ NYC",
          description: "ขั้นตอนสั่งแปลเอกสาร (ทะเบียนบ้าน · สูติบัตร · วุฒิการศึกษา · ทรานสคริปต์ · ใบรับรองโสด) แบบรับรองคำแปลที่ MFA สถานทูต ศาล และ ตม. ยอมรับ",
          totalTime: "PT2D", estimatedCost: { currency: "THB", value: "500" },
          steps: [
            { name: "ส่งภาพเอกสารทาง LINE @nycli", text: "ถ่ายรูปหรือสแกนต้นฉบับชัดเจน ส่งใน LINE @nycli แจ้งภาษาปลายทาง + วัตถุประสงค์ (ยื่นวีซ่า/จดสมรส/สมัครเรียน)", url: `${SITE}/quote` },
            { name: "รับใบเสนอราคา + ชำระมัดจำ 50%", text: "ทีมประเมินจำนวนหน้า + ความเร่งด่วน ภายใน 30 นาที — ราคาเริ่ม ฿500/หน้า รับรองโดย DBD-registered company" },
            { name: "ทีมแปลโดยนักแปลขึ้นทะเบียนกระทรวงยุติธรรม", text: "ใช้เวลา 1-3 วันทำการ (เร่งด่วน 24 ชม. ได้) — ทุกหน้ามีตราบริษัท + ลายเซ็นนักแปล Sworn" },
            { name: "รับไฟล์ PDF + เอกสารต้นฉบับ", text: "รับ PDF ส่งอีเมล + ชุดเอกสารต้นฉบับ EMS ทั่วประเทศ — พร้อมนำไปยื่น MFA/สถานทูตต่อได้ทันที", url: `${SITE}/apostille` },
          ],
        }),
        consolidateGraph([
          serviceWithReviewsSchema({ path: "/translate", serviceType: "Certified Document Translation", name: title, description: desc, priceFromThb: 500 }),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              "@id": `${url}#page`,
              name: title,
              description: desc,
              hasPart: TRANSLATION_LANGUAGES.map((l) => ({
                "@type": "WebPage",
                name: `แปล${l.nameTh}`,
                url: `${SITE}/translate/${l.slug}`,
                inLanguage: ["th", l.iso639],
              })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "แปลรายภาษา", item: url },
              ],
            }),
          },
        ]),
        entitySameAsSchema(),
        softwareApplicationSchema({
          url: `${SITE}/api/public/mcp`,
          name: "NYC Language Institute — Public MCP Server",
          description: "AI agent endpoint exposing 20+ typed tools for certified translation across 19+ languages.",
        }),
        claimReviewSchema(COMMON_CLAIM_REBUTTALS_TH(url)),
      
        ...(loaderData?.clusterFaq ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.clusterFaq) }] : []),
      ],
    };
  },
  component: TranslateIndex,
});

function TranslateIndex() {
  const { voiceFaqs } = Route.useLoaderData();
  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Languages className="h-3.5 w-3.5" /> {TRANSLATION_LANGUAGES.length}+ Languages
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            แปลเอกสารรายภาษา — รับรองใช้ยื่นได้ทันที
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            นักแปลขึ้นทะเบียน MoJ + NAATI + กงสุล ครบ {TRANSLATION_LANGUAGES.length} ภาษา · ราคาเริ่ม ฿500/หน้า · เสร็จ 1-5 วันทำการ
          </p>
          <div className="mt-6 grid gap-4 lg:grid-cols-[1.1fr_1fr]">
            <DirectAnswer entity="NYC Certified Translation Thailand">
              NYC Language Institute ให้บริการแปลเอกสารรับรองครบ {TRANSLATION_LANGUAGES.length}+ ภาษา โดยนักแปลที่ขึ้นทะเบียนกระทรวงยุติธรรม (MoJ), NAATI (Australia) และกรมการกงสุล กระทรวงต่างประเทศ — ยื่นได้ทุกหน่วยงาน: สถานทูต, ตม., อำเภอ, ศาล, มหาวิทยาลัย ราคาเริ่ม ฿500/หน้า เสร็จ 1–5 วันทำการ พร้อมต่อ Apostille / MFA / สถานทูตปลายทางได้ในจุดเดียว
            </DirectAnswer>
            <KeyFactsTable rows={[
              { k: "จำนวนภาษา", v: `${TRANSLATION_LANGUAGES.length}+ ภาษา` },
              { k: "ราคาเริ่มต้น", v: "฿500 / หน้า" },
              { k: "ระยะเวลา", v: "1–5 วันทำการ" },
              { k: "การรับรอง", v: "MoJ · NAATI · MFA · สถานทูต" },
              { k: "ใช้ยื่น", v: "วีซ่า · ตม. · อำเภอ · ศาล" },
              { k: "DBD", v: "0435567000061" },
            ]} />
          </div>
        </div>
      </section>

      <QuotableStatsStrip cluster="translate" locale="th" variant="dark" />

      <AnswerBoxTop cluster="translate" locale="th" />

      <section className="mx-auto max-w-5xl px-4 sm:px-6 -mt-6">
        <AIAnswerBox
          lang="th"
          id="ai-answer-translate"
          question="แปลเอกสารรับรองในไทย ราคาเท่าไร และใช้กี่วัน?"
          answer={`NYC Language Institute แปลรับรอง ${TRANSLATION_LANGUAGES.length}+ ภาษา เริ่ม ฿500/หน้า (ไทย-อังกฤษ), ฿800–2,500/หน้า (ภาษาอื่น), NAATI ฿1,300–2,500/หน้า เสร็จ 1–5 วันทำการ — ยื่นได้ทั้ง MFA, สถานทูต, ตม., อำเภอ, ศาล, มหาวิทยาลัย โดยนักแปลขึ้นทะเบียน MoJ + NAATI + กงสุล`}
          stats={[
            { label: "ภาษา", value: `${TRANSLATION_LANGUAGES.length}+` },
            { label: "ราคาเริ่ม", value: "฿500/หน้า" },
            { label: "ระยะเวลา", value: "1–5 วัน" },
            { label: "ผ่านรอบแรก", value: "99%+" },
          ]}
          sourceUrl="https://nyclanguageinstitute.com/translate"
        />
      </section>



      <section className="mx-auto max-w-6xl px-4 sm:px-6 mt-8 sm:mt-10">
        <VoiceSearchFAQ topic="Certified Translation Thailand" faqs={voiceFaqs} entity="NYC Certified Translation Thailand" />
      </section>

      <section className="mx-auto max-w-6xl px-4 pt-12 sm:px-6 sm:pt-16">
        <h2 className="font-display text-xl font-extrabold text-navy-deep sm:text-2xl">รับรองคำแปลตามมาตรฐานปลายทาง</h2>
        <p className="mt-1 text-sm text-slate-600">เลือกประเภทการรับรองที่หน่วยงานปลายทางต้องการ</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <Link to="/translate/naati" className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 transition hover:border-emerald-400 hover:shadow-md">
            <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">NAATI Certified</div>
            <div className="mt-1 font-display text-base font-extrabold text-navy-deep">แปลรับรอง NAATI (AU/NZ)</div>
            <div className="mt-1 text-xs text-slate-600">DHA · DFAT · INZ · BDM · NZQA</div>
          </Link>
          <Link to="/translate/mfa" className="rounded-xl border border-amber-200 bg-amber-50/40 p-4 transition hover:border-amber-400 hover:shadow-md">
            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">MFA Legalized</div>
            <div className="mt-1 font-display text-base font-extrabold text-navy-deep">รับรองคำแปลที่กงสุล (MFA)</div>
            <div className="mt-1 text-xs text-slate-600">แจ้งวัฒนะ · 2-5 วันทำการ</div>
          </Link>
          <Link to="/translate/embassy-legalization" className="rounded-xl border border-sky-200 bg-sky-50/40 p-4 transition hover:border-sky-400 hover:shadow-md">
            <div className="text-[10px] font-bold uppercase tracking-widest text-sky-700">Embassy Legalized</div>
            <div className="mt-1 font-display text-base font-extrabold text-navy-deep">รับรองสถานทูตปลายทาง</div>
            <div className="mt-1 text-xs text-slate-600">CN · UAE · SA · MM · RU · EG</div>
          </Link>
          <Link to="/translate/government" className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4 transition hover:border-indigo-400 hover:shadow-md">
            <div className="text-[10px] font-bold uppercase tracking-widest text-indigo-700">Government Docs</div>
            <div className="mt-1 font-display text-base font-extrabold text-navy-deep">แปลเอกสารราชการไทย</div>
            <div className="mt-1 text-xs text-slate-600">ทะเบียนบ้าน · สูติบัตร · DBD · สด.43 · ฿500/หน้า</div>
          </Link>
          <Link to="/translate/visa-docs" className="rounded-xl border border-rose-200 bg-rose-50/40 p-4 transition hover:border-rose-400 hover:shadow-md">
            <div className="text-[10px] font-bold uppercase tracking-widest text-rose-700">Visa Pack</div>
            <div className="mt-1 font-display text-base font-extrabold text-navy-deep">แปลชุดเอกสารวีซ่า</div>
            <div className="mt-1 text-xs text-slate-600">Bank · Payslip · ทะเบียนบ้าน · ชุดครบ ฿4,500</div>
          </Link>
          <Link to="/translate/naati/online" className="rounded-xl border border-teal-200 bg-teal-50/40 p-4 transition hover:border-teal-400 hover:shadow-md">
            <div className="text-[10px] font-bold uppercase tracking-widest text-teal-700">NAATI Online</div>
            <div className="mt-1 font-display text-base font-extrabold text-navy-deep">NAATI ออนไลน์ 50 ประเทศ</div>
            <div className="mt-1 text-xs text-slate-600">ส่ง PDF + DHL ทั่วโลก</div>
          </Link>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {TRANSLATION_LANGUAGES.map((l) => (
            <Link
              key={l.slug}
              to="/translate/$language"
              params={{ language: l.slug }}
              className="group rounded-2xl border border-slate-200 bg-white p-5 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-lg"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{l.nameEn}{l.nativeName ? ` · ${l.nativeName}` : ""}</div>
                  <div className="mt-1 font-display text-lg font-extrabold text-navy-deep group-hover:text-amber-600">แปล{l.nameTh}</div>
                </div>
                <ArrowRight className="h-5 w-5 shrink-0 text-slate-400 transition group-hover:translate-x-1 group-hover:text-amber-600" />
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-slate-600">
                <span>เริ่ม <span className="font-bold text-navy-deep">฿{l.priceFromTh.toLocaleString()}</span>/หน้า</span>
                <span className="text-slate-300">·</span>
                <span>{l.turnaround}</span>
                {l.isNaati && <><span className="text-slate-300">·</span><span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">NAATI</span></>}
              </div>
              <div className="mt-3 text-[11px] text-slate-500">ใช้ใน: {l.countryOfUse.slice(0, 3).join(" · ")}</div>
            </Link>
          ))}
        </div>
      </section>

      <TranslateVisaCrossLink mode="from-translate" locale="th" />
      <RelatedComparisons category="translate" locale="th" />
      <TrustLayer variant="compact" />
      <HubCountryLinks service="apostille" lang="th" />
          <ServicePageEnhancer serviceName="แปลเอกสาร" locale="th" />

      <PlanningToolsLinks locale="th" />
      </main>
  );
}
